import React, { useState, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";

import AddIcon from "@material-ui/icons/Add";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ScheduleIcon from "@material-ui/icons/Schedule";
import BlockIcon from "@material-ui/icons/Block";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import WarningIcon from "@material-ui/icons/Warning";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "./apiKeys.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const API_KEY_FIELDS = `
  _id
  name
  prefix
  last4
  scopes
  state
  expiresAt
  revokedAt
  ipAllowList
  rateLimitPerMinute
  lastUsedAt
  lastUsedIp
  usageCount
  createdAt
`;

// getApiKeysByShopAdmin takes pagination and sort but no filters, so searching,
// filtering and sorting are all done on the client over one fetched window. A
// shop is capped at MAX_ACTIVE_KEYS live keys and only accumulates revoked ones,
// so the whole set fits comfortably in a single request.
const GET_API_KEYS = gql`
  query getApiKeysByShopAdmin($limit: Int, $skip: Int) {
    getApiKeysByShopAdmin(
      pagination: { limit: $limit, skip: $skip }
      sort: { createdAt: -1 }
    ) {
      ${API_KEY_FIELDS}
    }
    getApiKeysByShopAdminCount
  }
`;

const CREATE_API_KEY = gql`
  mutation createApiKeyByShopAdmin($input: CreateApiKeyInput!) {
    createApiKeyByShopAdmin(input: $input) {
      key
      apiKey {
        ${API_KEY_FIELDS}
      }
    }
  }
`;

const UPDATE_API_KEY = gql`
  mutation updateApiKeyByShopAdmin($id: ID!, $input: UpdateApiKeyInput!) {
    updateApiKeyByShopAdmin(id: $id, input: $input) {
      ${API_KEY_FIELDS}
    }
  }
`;

const REVOKE_API_KEY = gql`
  mutation revokeApiKeyByShopAdmin($id: ID!) {
    revokeApiKeyByShopAdmin(id: $id) {
      _id
      state
      revokedAt
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 *
 * These mirror the backend defaults (API_KEY_MAX_ACTIVE_PER_SHOP and
 * PARTNER_API_RATE_LIMIT). They are only used to fail fast in the UI; the
 * server is still the authority and its error is surfaced either way.
 * ------------------------------------------------------------------ */

const MAX_ACTIVE_KEYS = 5;
const MAX_RATE_LIMIT_PER_MINUTE = 120;
const FETCH_LIMIT = 200;

const SCOPE_OPTIONS = [
  {
    value: "PRODUCTS_READ",
    label: "Read products",
    description: "List and read the shop catalogue.",
  },
  {
    value: "PRODUCTS_WRITE",
    label: "Write products",
    description: "Create, update and delete products.",
  },
];

const SCOPE_LABELS = {
  PRODUCTS_READ: "products:read",
  PRODUCTS_WRITE: "products:write",
};

const SORT_OPTIONS = [
  { value: "createdDesc", label: "Newest first" },
  { value: "createdAsc", label: "Oldest first" },
  { value: "lastUsedDesc", label: "Recently used" },
  { value: "nameAsc", label: "Name A-Z" },
];

const SORT_LABELS = SORT_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const emptyForm = () => ({
  name: "",
  scopes: ["PRODUCTS_READ"],
  expiresAt: "",
  ipAllowList: "",
  rateLimitPerMinute: "",
});

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

// The backend validates with net.isIP, which takes bare addresses and not CIDR
// ranges. This mirrors that closely enough to catch typos before a round trip.
const isValidIp = (value) => {
  const candidate = String(value).trim();
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(candidate)) {
    return candidate.split(".").every((part) => {
      const asNumber = Number(part);
      return String(asNumber) === part && asNumber >= 0 && asNumber <= 255;
    });
  }
  return candidate.indexOf(":") !== -1 && /^[0-9a-fA-F:.]+$/.test(candidate);
};

const parseIpAllowList = (raw) =>
  String(raw)
    .split(/[\s,]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const isExpired = (apiKey) =>
  Boolean(apiKey.expiresAt) &&
  new Date(apiKey.expiresAt).getTime() <= Date.now();

// A key that has expired is still ACTIVE in the database (expiry is enforced at
// authentication time), so it keeps counting against the per shop limit. It is
// shown as its own state because "Active" would be misleading.
const describeState = (apiKey) => {
  if (apiKey.state === "REVOKED") return { status: "danger", label: "Revoked" };
  if (isExpired(apiKey)) return { status: "warning", label: "Expired" };
  return { status: "success", label: "Active" };
};

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

const copyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  // execCommand is deprecated but is the only option on http origins and on the
  // older browsers this panel still supports.
  return new Promise((resolve, reject) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const ShopApiKeys = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [sortOption, setSortOption] = useState("createdDesc");
  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [createdKey, setCreatedKey] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState("");
  const [copied, setCopied] = useState(false);
  const [actionError, setActionError] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_API_KEYS, {
    variables: { limit: FETCH_LIMIT, skip: 0 },
    fetchPolicy: "network-only",
  });

  const [createApiKey, { loading: creating }] = useMutation(CREATE_API_KEY);
  const [updateApiKey, { loading: updating }] = useMutation(UPDATE_API_KEY);
  const [revokeApiKey, { loading: revoking }] = useMutation(REVOKE_API_KEY);

  const apiKeys = (data && data.getApiKeysByShopAdmin) || [];
  const totalCount = (data && data.getApiKeysByShopAdminCount) || 0;

  // The limit counts every ACTIVE row, expired ones included, exactly as the
  // backend counts them.
  const activeCount = apiKeys.filter((key) => key.state === "ACTIVE").length;
  const expiredCount = apiKeys.filter(
    (key) => key.state === "ACTIVE" && isExpired(key),
  ).length;
  const revokedCount = apiKeys.filter((key) => key.state === "REVOKED").length;
  const limitReached = activeCount >= MAX_ACTIVE_KEYS;

  const visibleKeys = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = apiKeys.filter((key) => {
      if (term) {
        const haystack = [key.name, key.prefix, key.last4]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (haystack.indexOf(term) === -1) return false;
      }
      if (statusFilter) {
        const { label } = describeState(key);
        if (label.toLowerCase() !== statusFilter) return false;
      }
      if (scopeFilter) {
        if (!key.scopes || key.scopes.indexOf(scopeFilter) === -1) return false;
      }
      return true;
    });

    const byDate = (value) => (value ? new Date(value).getTime() : 0);

    return filtered.sort((a, b) => {
      switch (sortOption) {
        case "createdAsc":
          return byDate(a.createdAt) - byDate(b.createdAt);
        case "lastUsedDesc":
          return byDate(b.lastUsedAt) - byDate(a.lastUsedAt);
        case "nameAsc":
          return String(a.name || "").localeCompare(String(b.name || ""));
        case "createdDesc":
        default:
          return byDate(b.createdAt) - byDate(a.createdAt);
      }
    });
  }, [apiKeys, searchTerm, statusFilter, scopeFilter, sortOption]);

  const pageStart = pageCount * perPage;
  const currentPageKeys = visibleKeys.slice(pageStart, pageStart + perPage);

  const activeFilterChips = [];
  if (searchTerm.trim()) {
    activeFilterChips.push({ id: "search", label: `Search: ${searchTerm}` });
  }
  if (statusFilter) {
    activeFilterChips.push({ id: "status", label: `Status: ${statusFilter}` });
  }
  if (scopeFilter) {
    activeFilterChips.push({
      id: "scope",
      label: `Scope: ${SCOPE_LABELS[scopeFilter] || scopeFilter}`,
    });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "status") setStatusFilter("");
    if (id === "scope") setScopeFilter("");
    setPageCount(0);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setScopeFilter("");
    setPageCount(0);
  };

  /* ---------------- form handling ---------------- */

  const setFormValue = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleScope = (scope) => {
    setForm((current) => {
      const selected = current.scopes.indexOf(scope) !== -1;
      return {
        ...current,
        scopes: selected
          ? current.scopes.filter((item) => item !== scope)
          : current.scopes.concat(scope),
      };
    });
  };

  // Returns the mutation input, or null after setting a message on the form.
  const buildInput = ({ requireName }) => {
    const name = form.name.trim();
    if (requireName && !name) {
      setFormError("Give the key a name so you can recognise it later.");
      return null;
    }
    if (form.scopes.length === 0) {
      setFormError("Select at least one scope.");
      return null;
    }

    const ipAllowList = parseIpAllowList(form.ipAllowList);
    const invalidIp = ipAllowList.find((ip) => !isValidIp(ip));
    if (invalidIp) {
      setFormError(`"${invalidIp}" is not a valid IP address.`);
      return null;
    }

    let rateLimitPerMinute;
    if (String(form.rateLimitPerMinute).trim()) {
      rateLimitPerMinute = Number(form.rateLimitPerMinute);
      if (!Number.isFinite(rateLimitPerMinute) || rateLimitPerMinute < 1) {
        setFormError("Rate limit must be a positive number.");
        return null;
      }
      if (rateLimitPerMinute > MAX_RATE_LIMIT_PER_MINUTE) {
        setFormError(
          `Rate limit cannot be higher than ${MAX_RATE_LIMIT_PER_MINUTE} requests per minute.`,
        );
        return null;
      }
    }

    let expiresAt;
    if (form.expiresAt) {
      // The picker gives a day, the key stays usable until the end of it.
      const parsed = moment(form.expiresAt, "YYYY-MM-DD").endOf("day");
      if (!parsed.isValid()) {
        setFormError("Expiry date is not valid.");
        return null;
      }
      if (parsed.valueOf() <= Date.now()) {
        setFormError("Expiry date must be in the future.");
        return null;
      }
      expiresAt = parsed.toDate();
    }

    const input = { scopes: form.scopes, ipAllowList };
    if (name || requireName) input.name = name;
    if (expiresAt) input.expiresAt = expiresAt;
    if (rateLimitPerMinute) input.rateLimitPerMinute = rateLimitPerMinute;
    return input;
  };

  const openCreateDialog = () => {
    setForm(emptyForm());
    setFormError("");
    setActionError("");
    setCreateOpen(true);
  };

  const openEditDialog = (apiKey) => {
    setForm({
      name: apiKey.name || "",
      scopes: apiKey.scopes ? apiKey.scopes.slice() : [],
      expiresAt: apiKey.expiresAt
        ? moment(apiKey.expiresAt).format("YYYY-MM-DD")
        : "",
      ipAllowList: (apiKey.ipAllowList || []).join(", "),
      rateLimitPerMinute:
        apiKey.rateLimitPerMinute == null
          ? ""
          : String(apiKey.rateLimitPerMinute),
    });
    setFormError("");
    setActionError("");
    setEditTarget(apiKey);
  };

  const handleCreate = () => {
    if (creating) return;
    const input = buildInput({ requireName: true });
    if (!input) return;

    createApiKey({ variables: { input } })
      .then((response) => {
        const created = response.data.createApiKeyByShopAdmin;
        setCreateOpen(false);
        setForm(emptyForm());
        setCopied(false);
        // The only moment the plain key exists in the browser. It is dropped
        // from state as soon as the reveal dialog is closed.
        setCreatedKey(created);
        return refetch();
      })
      .catch((err) => setFormError(readableError(err)));
  };

  const handleUpdate = () => {
    if (updating || !editTarget) return;
    const input = buildInput({ requireName: false });
    if (!input) return;

    updateApiKey({ variables: { id: editTarget._id, input } })
      .then(() => {
        setEditTarget(null);
        setForm(emptyForm());
        return refetch();
      })
      .catch((err) => setFormError(readableError(err)));
  };

  const handleRevoke = () => {
    if (revoking || !revokeTarget) return;
    revokeApiKey({ variables: { id: revokeTarget._id } })
      .then(() => {
        setRevokeTarget(null);
        return refetch();
      })
      .catch((err) => {
        setRevokeTarget(null);
        setActionError(readableError(err));
      });
  };

  const closeReveal = () => {
    setCreatedKey(null);
    setCopied(false);
  };

  const handleCopy = () => {
    if (!createdKey) return;
    copyToClipboard(createdKey.key)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  /* ---------------- render ---------------- */

  // A skeleton in the shape of the page keeps the layout still, instead
  // of jumping from a centred spinner straight to a full table.
  if (loading && !data) {
    return (
      <div className="page apiKeysPage">
        <main className="main">
          <PageHeader
            title="API keys"
            description="Grant your own systems programmatic access to this shop."
          />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const scopeFields = (
    <Field label="Scopes" required>
      <div className="apiKeyScopeOptions">
        {SCOPE_OPTIONS.map((option) => {
          const selected = form.scopes.indexOf(option.value) !== -1;
          return (
            <label
              key={option.value}
              className={`dsOptionCard ${selected ? "isSelected" : ""}`}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => toggleScope(option.value)}
              />
              <span className="dsOptionCardText">
                <strong>{option.label}</strong>
                <span>{option.description}</span>
              </span>
            </label>
          );
        })}
      </div>
    </Field>
  );

  const optionalFields = (
    <React.Fragment>
      <Field
        label="Expires on"
        optional
        htmlFor="apiKeyExpiresAt"
        hint="The key stops working at the end of this day."
      >
        <input
          id="apiKeyExpiresAt"
          type="date"
          className="dsInput"
          value={form.expiresAt}
          min={moment().add(1, "day").format("YYYY-MM-DD")}
          onChange={(e) => setFormValue("expiresAt", e.target.value)}
        />
      </Field>

      <Field
        label="IP allow list"
        optional
        htmlFor="apiKeyIpAllowList"
        hint="Separate addresses with commas. Leave empty to allow any address. Ranges are not supported."
      >
        <textarea
          id="apiKeyIpAllowList"
          className="dsInput apiKeyMonoInput"
          value={form.ipAllowList}
          placeholder="203.0.113.10, 203.0.113.11"
          onChange={(e) => setFormValue("ipAllowList", e.target.value)}
        />
      </Field>

      <Field
        label="Rate limit"
        optional
        htmlFor="apiKeyRateLimit"
        hint={`Requests per minute, up to ${MAX_RATE_LIMIT_PER_MINUTE}.`}
      >
        <input
          id="apiKeyRateLimit"
          type="number"
          className="dsInput"
          min="1"
          max={MAX_RATE_LIMIT_PER_MINUTE}
          value={form.rateLimitPerMinute}
          placeholder={String(MAX_RATE_LIMIT_PER_MINUTE)}
          onChange={(e) => setFormValue("rateLimitPerMinute", e.target.value)}
        />
      </Field>

      {formError && (
        <div className="dsFormMessage error" role="alert">
          {formError}
        </div>
      )}
    </React.Fragment>
  );

  return (
    <div className="page apiKeysPage">
      <main className="main">
        {/* HEADER */}
        <PageHeader
          title="API keys"
          count={totalCount || apiKeys.length}
          description="Grant your own systems programmatic access to this shop's catalogue. Each key carries its own scopes, expiry and rate limit."
          actions={
            <PageActionButton
              variant="primary"
              icon={<AddIcon fontSize="small" />}
              label="Create API key"
              info={
                limitReached
                  ? `Limit of ${MAX_ACTIVE_KEYS} active keys reached`
                  : `${MAX_ACTIVE_KEYS - activeCount} of ${MAX_ACTIVE_KEYS} remaining`
              }
              onClick={openCreateDialog}
              disabled={limitReached}
            />
          }
        />

        {/* STATS */}
        <section className="stats">
          <MetricCard
            icon={<VpnKeyIcon />}
            title="Total keys"
            value={totalCount || apiKeys.length}
          />
          <MetricCard
            icon={<CheckCircleOutlineIcon />}
            title="Active"
            value={`${activeCount} / ${MAX_ACTIVE_KEYS}`}
            tone={limitReached ? "warning" : "success"}
          />
          <MetricCard
            icon={<ScheduleIcon />}
            title="Expired"
            value={expiredCount}
            tone={expiredCount > 0 ? "warning" : "default"}
            hint={
              expiredCount > 0
                ? "Still counts towards the limit"
                : "None past their expiry"
            }
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Revoked"
            value={revokedCount}
            hint="Kept for your audit trail"
          />
        </section>

        {/* FILTERS */}
        <section className="filters">
          <SearchField
            placeholder="Search by name or key prefix..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPageCount(0);
            }}
          />
          <FilterDropdown
            label={statusFilter ? `Status: ${statusFilter}` : "Status"}
            options={[
              { value: "", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "expired", label: "Expired" },
              { value: "revoked", label: "Revoked" },
            ]}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPageCount(0);
            }}
          />
          <FilterDropdown
            label={
              scopeFilter
                ? `Scope: ${SCOPE_LABELS[scopeFilter] || scopeFilter}`
                : "Scope"
            }
            options={[
              { value: "", label: "All scopes" },
              { value: "PRODUCTS_READ", label: "products:read" },
              { value: "PRODUCTS_WRITE", label: "products:write" },
            ]}
            value={scopeFilter}
            onChange={(e) => {
              setScopeFilter(e.target.value);
              setPageCount(0);
            }}
          />
          <FilterDropdown
            label={`Sort: ${SORT_LABELS[sortOption]}`}
            options={SORT_OPTIONS}
            value={sortOption}
            /* Sort always holds a value but is not narrowing anything, so
               it should not read as an applied filter. */
            active={false}
            align="right"
            onChange={(e) => {
              setSortOption(e.target.value);
              setPageCount(0);
            }}
          />
        </section>

        {/* TOOLBAR */}
        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            <ActiveFilterChipList
              filters={activeFilterChips}
              onClearFilter={clearFilter}
              onClearAll={clearAllFilters}
            />
          </div>
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh API keys"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        {/* TABLE */}
        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Name</div>
              <div className="tableHeaderCell">Key</div>
              <div className="tableHeaderCell">Scopes</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Last used</div>
              <div className="tableHeaderCell">Created</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {currentPageKeys.length === 0 ? (
              <EmptyState
                icon={<VpnKeyIcon />}
                title={apiKeys.length === 0 ? "No API keys yet" : "No matches"}
                message={
                  apiKeys.length === 0
                    ? "Create a key to let your own systems read and manage this shop's products."
                    : "No API keys match these filters. Try widening or clearing them."
                }
                action={
                  apiKeys.length === 0 ? (
                    <Button
                      variant="primary"
                      icon={<AddIcon fontSize="small" />}
                      onClick={openCreateDialog}
                      disabled={limitReached}
                    >
                      Create API key
                    </Button>
                  ) : (
                    <Button onClick={clearAllFilters}>Clear filters</Button>
                  )
                }
              />
            ) : (
              currentPageKeys.map((apiKey) => {
                const state = describeState(apiKey);
                return (
                  <div className="row" key={apiKey._id}>
                    <div className="keyName">
                      <h4>{apiKey.name || "Untitled key"}</h4>
                      <p>
                        {apiKey.usageCount
                          ? `${apiKey.usageCount} requests`
                          : "Never used"}
                      </p>
                    </div>

                    <span className="keyFingerprint" data-label="Key">
                      {apiKey.prefix}&hellip;{apiKey.last4}
                    </span>

                    <span className="scopeChips" data-label="Scopes">
                      {(apiKey.scopes || []).map((scope) => (
                        <span
                          key={scope}
                          className={`apiKeyScopeChip ${
                            scope === "PRODUCTS_WRITE" ? "write" : "read"
                          }`}
                        >
                          {SCOPE_LABELS[scope] || scope}
                        </span>
                      ))}
                    </span>

                    <span data-label="Status">
                      <StatusBadge status={state.status} label={state.label} />
                    </span>

                    <span className="updated" data-label="Last used">
                      {apiKey.lastUsedAt ? (
                        <React.Fragment>
                          <strong>{formatDate(apiKey.lastUsedAt)}</strong>
                          <span>{formatTime(apiKey.lastUsedAt)}</span>
                        </React.Fragment>
                      ) : (
                        <strong>Never</strong>
                      )}
                    </span>

                    <span className="updated" data-label="Created">
                      <strong>{formatDate(apiKey.createdAt)}</strong>
                      <span>{formatTime(apiKey.createdAt)}</span>
                    </span>

                    <div className="actions">
                      {apiKey.state === "ACTIVE" && (
                        <React.Fragment>
                          <IconButton
                            size="small"
                            className="rowActionButton"
                            onClick={() => openEditDialog(apiKey)}
                            title="Edit key"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            className="rowActionButton"
                            onClick={() => setRevokeTarget(apiKey)}
                            title="Revoke key"
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {/* PAGINATION */}
        {visibleKeys.length > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={visibleKeys.length}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(size);
              setPageCount(0);
            }}
            info={`Showing ${Math.min(pageStart + 1, visibleKeys.length)} to ${Math.min(
              pageStart + perPage,
              visibleKeys.length,
            )} of ${visibleKeys.length}`}
          />
        )}
      </main>

      {/* CREATE */}
      <DialogShell
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create API key"
        subtitle="The secret is shown once, right after it is created."
        actions={[
          { label: "Cancel", onClick: () => setCreateOpen(false) },
          {
            label: "Create key",
            onClick: handleCreate,
            variant: "primary",
            loading: creating,
          },
        ]}
      >
        <div className="apiKeyForm">
          <Field
            label="Name"
            required
            htmlFor="apiKeyName"
            hint="Only you see this. Name it after the system that will use it."
          >
            <input
              id="apiKeyName"
              type="text"
              className="dsInput"
              value={form.name}
              placeholder="warehouse sync"
              onChange={(e) => setFormValue("name", e.target.value)}
            />
          </Field>
          {scopeFields}
          {optionalFields}
        </div>
      </DialogShell>

      {/* ONE TIME REVEAL */}
      <DialogShell
        open={Boolean(createdKey)}
        onClose={closeReveal}
        title="Copy your API key"
        actions={[{ label: "Done", onClick: closeReveal, variant: "primary" }]}
      >
        {createdKey && (
          <div className="apiKeyReveal">
            <div className="dsFormMessage warning">
              <WarningIcon fontSize="small" />
              <span>
                This is the only time the key is shown. It is not stored and
                cannot be retrieved again. Copy it somewhere safe before closing
                this dialog.
              </span>
            </div>

            <div className="apiKeySecretBox">
              <code>{createdKey.key}</code>
              <Button
                size="sm"
                variant={copied ? "primary" : "secondary"}
                icon={<FileCopyIcon style={{ fontSize: 14 }} />}
                onClick={handleCopy}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>

            <div className="apiKeyRevealMeta">
              <span className="apiKeyRevealMetaLabel">Name</span>
              <span className="apiKeyRevealMetaValue">
                {createdKey.apiKey.name}
              </span>
              <span className="apiKeyRevealMetaLabel">Scopes</span>
              <span className="apiKeyRevealMetaValue">
                {(createdKey.apiKey.scopes || [])
                  .map((scope) => SCOPE_LABELS[scope] || scope)
                  .join(", ")}
              </span>
              <span className="apiKeyRevealMetaLabel">Expires</span>
              <span className="apiKeyRevealMetaValue">
                {createdKey.apiKey.expiresAt
                  ? formatDate(createdKey.apiKey.expiresAt)
                  : "Never"}
              </span>
            </div>
          </div>
        )}
      </DialogShell>

      {/* EDIT */}
      <DialogShell
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        title="Edit API key"
        actions={[
          { label: "Cancel", onClick: () => setEditTarget(null) },
          {
            label: "Save changes",
            onClick: handleUpdate,
            variant: "primary",
            loading: updating,
          },
        ]}
      >
        <div className="apiKeyForm">
          <Field label="Name" htmlFor="apiKeyEditName">
            <input
              id="apiKeyEditName"
              type="text"
              className="dsInput"
              value={form.name}
              onChange={(e) => setFormValue("name", e.target.value)}
            />
          </Field>
          {scopeFields}
          {optionalFields}
          <p className="apiKeyDialogNote">
            The secret never changes. To replace it, create a new key and revoke
            this one.
          </p>
        </div>
      </DialogShell>

      {/* REVOKE */}
      <DialogShell
        open={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        title="Revoke API key"
        actions={[
          { label: "Cancel", onClick: () => setRevokeTarget(null) },
          {
            label: "Revoke key",
            onClick: handleRevoke,
            variant: "danger",
            loading: revoking,
          },
        ]}
        size="sm"
      >
        {revokeTarget && (
          <div className="apiKeyConfirmBody">
            <span>
              <strong>{revokeTarget.name || "This key"}</strong> (
              {revokeTarget.prefix}&hellip;{revokeTarget.last4}) stops working
              immediately. Anything using it will start failing straight away.
            </span>
            <span>
              This cannot be undone. To restore access you will need to create a
              new key and update the system that uses it.
            </span>
          </div>
        )}
      </DialogShell>
    </div>
  );
};

export default ShopApiKeys;
